import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquare, Users } from "lucide-react";

import { Avatar, AvatarFallback } from "../ui/avatar";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import openSocket from "../../services/socket-io";

// O chat interno dentro da lista de conversas.
//
// Ele existia só numa página à parte do menu: quem estava atendendo não via
// que um colega tinha chamado, porque a tela de atendimento não fala do chat.
// Aqui as conversas da equipe ficam na mesma coluna dos atendimentos — a fila
// do cliente e o recado do colega no mesmo lugar, que é onde se olha.
//
// A lista é só a porta de entrada: clicar leva ao chat, que continua sendo a
// tela existente. Duplicar a interface de conversa aqui dentro seria manter
// duas.
const iniciais = (nome = "?") =>
	nome
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((parte) => parte[0])
		.join("")
		.toUpperCase() || "?";

// A API devolve a última mensagem inteira, não um texto. Sem isto o React
// recebe um objeto como filho e derruba a tela de atendimentos (erro #31).
const resumoDaMensagem = (mensagem) => {
	if (!mensagem) return "Sem mensagens";
	if (mensagem.isDeleted) return "Mensagem apagada";

	const corpo = String(mensagem.body || "").trim();
	if (corpo) return corpo;

	if (mensagem.mediaUrl) {
		const tipo = String(mensagem.mediaType || "");
		if (tipo.startsWith("image")) return "Imagem";
		if (tipo.startsWith("video")) return "Vídeo";
		if (tipo.startsWith("audio")) return "Áudio";
		return "Arquivo";
	}

	return "Sem mensagens";
};

const ListaDoChatInterno = ({ aoContar }) => {
	const navigate = useNavigate();
	const [conversas, setConversas] = useState([]);
	const [carregando, setCarregando] = useState(true);

	const carregar = useCallback(
		(ativo = { current: true }) =>
			api
				.get("/internal-chat/conversations")
				.then(({ data }) => {
					if (!ativo.current) return;

					const lista = Array.isArray(data) ? data : [];
					setConversas(lista);
					setCarregando(false);

					// A contagem vai para a aba: é o que faz o número aparecer sem
					// precisar entrar aqui.
					aoContar?.(
						lista.reduce((total, c) => total + (c.unreadCount || 0), 0),
					);
				})
				.catch(() => setCarregando(false)),
		[aoContar],
	);

	useEffect(() => {
		const ativo = { current: true };
		Promise.resolve().then(() => carregar(ativo));

		// Mensagem nova do colega atualiza a lista na hora, como acontece com
		// os atendimentos.
		const socket = openSocket();
		socket.on("internalChat:message", () => carregar(ativo));
		socket.on("internalChat:group", () => carregar(ativo));

		return () => {
			ativo.current = false;
			socket.disconnect();
		};
	}, [carregar]);

	if (carregando) {
		return (
			<div className="flex justify-center py-10">
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!conversas.length) {
		return (
			<div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
				<MessageSquare className="h-8 w-8 text-muted-foreground/60" />
				<p className="text-sm font-medium">Nenhuma conversa interna</p>
				<p className="text-xs text-muted-foreground">
					Abra o chat da equipe para falar com um colega.
				</p>
			</div>
		);
	}

	return (
		<ul className="divide-y">
			{conversas.map((conversa) => {
				const nome =
					conversa.type === "group" ? conversa.groupName : conversa.userName;
				const naoLidas = conversa.unreadCount || 0;

				return (
					<li key={`${conversa.type}-${conversa.groupId || conversa.userId}`}>
						<button
							type="button"
							onClick={() => navigate("/internal-chat")}
							className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/60"
						>
							<Avatar className="h-9 w-9 shrink-0">
								<AvatarFallback className="text-xs">
									{conversa.type === "group" ? (
										<Users className="h-4 w-4" />
									) : (
										iniciais(nome)
									)}
								</AvatarFallback>
							</Avatar>

							<span className="min-w-0 flex-1">
								<span
									className={cn(
										"block truncate text-sm",
										naoLidas > 0 && "font-semibold",
									)}
								>
									{nome || "Conversa"}
								</span>
								<span className="block truncate text-xs text-muted-foreground">
									{resumoDaMensagem(conversa.lastMessage)}
								</span>
							</span>

							{naoLidas > 0 && (
								<span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-medium text-primary-foreground">
									{naoLidas}
								</span>
							)}
						</button>
					</li>
				);
			})}
		</ul>
	);
};

export default ListaDoChatInterno;
